import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Wallet } from 'lucide-react';
import { EmptyState } from '../components/feedback/empty-state.js';
import { PageHeader } from '../components/layout/page-header.js';
import { Badge, Button, ListSkeleton } from '@tailoring-crm/ui';
import { useMyEarnings } from '../lib/payouts/use-payouts.js';
import { formatCurrency, formatDate } from '../lib/format.js';

/**
 * The karigar's own money, on their own phone.
 *
 * This screen is the other half of transparency. The owner has a settlement screen; the person
 * doing the work has THIS — the same jobs, the same rates, the same three figures — so that
 * "what am I owed?" is a question they can answer themselves at nine at night without asking
 * anybody. A tailor who cannot check the number will not trust it.
 *
 * It shows their own figures and nobody else's, and that is enforced by the endpoint rather than
 * by this page: `/payouts/me` takes no id at all and is pinned to the session.
 */
export function MyEarningsPage(): ReactElement {
  const { t } = useTranslation();
  const earnings = useMyEarnings();

  const data = earnings.data;

  return (
    <>
      <PageHeader title={t('myEarnings.title')} description={t('myEarnings.description')} />

      {earnings.isPending ? (
        <ListSkeleton rows={4} />
      ) : !data ? (
        <EmptyState
          icon={Wallet}
          title={t('myEarnings.emptyTitle')}
          description={t('myEarnings.emptyHint')}
        />
      ) : (
        <div className="flex flex-col gap-5">
          {/* The figure they came for, first and largest. */}
          <div className="rounded-lg border border-border p-4" role="status">
            <p className="text-sm text-muted-foreground">{t('myEarnings.owedToYou')}</p>
            <p className="text-3xl font-semibold tabular-nums">
              {formatCurrency(data.summary.outstandingPaise)}
            </p>
            {data.summary.advanceOutstandingPaise > 0 ? (
              <p className="text-sm text-muted-foreground">
                {t('myEarnings.advanceNote', {
                  amount: formatCurrency(data.summary.advanceOutstandingPaise),
                })}
              </p>
            ) : null}
          </div>

          <dl className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-border p-3">
              <dt className="text-sm text-muted-foreground">{t('myEarnings.earned')}</dt>
              <dd className="text-xl font-semibold tabular-nums">
                {formatCurrency(data.summary.earnedPaise)}
              </dd>
            </div>
            <div className="rounded-lg border border-border p-3">
              <dt className="text-sm text-muted-foreground">{t('myEarnings.paid')}</dt>
              <dd className="text-xl font-semibold tabular-nums">
                {formatCurrency(data.summary.paidPaise)}
              </dd>
            </div>
          </dl>

          {/* --- What is not yet paid, job by job ------------------------------------------ */}
          <section className="flex flex-col gap-2">
            <h2 className="text-sm font-semibold">{t('myEarnings.notYetPaid')}</h2>
            {data.unsettled.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('myEarnings.nothingPending')}</p>
            ) : (
              <ul className="flex flex-col gap-2">
                {data.unsettled.map((job) => (
                  <li
                    key={job.assignmentId}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border p-3"
                  >
                    <span className="min-w-0">
                      <span className="block font-medium">
                        {job.quantity} × {job.garmentTypeLabel} · {t(`stage.${job.stage}`)}
                      </span>
                      <span className="block text-sm text-muted-foreground">
                        {t('payouts.finishedOn', { date: formatDate(job.completedAt) })}
                      </span>
                    </span>
                    <span className="text-right">
                      {job.amountPaise === null ? (
                        <Badge variant="warning">{t('myEarnings.unpriced')}</Badge>
                      ) : (
                        <>
                          <span className="block font-semibold tabular-nums">
                            {formatCurrency(job.amountPaise)}
                          </span>
                          <span className="block text-xs text-muted-foreground tabular-nums">
                            {t('payouts.rateEach', { rate: formatCurrency(job.ratePaise ?? 0) })}
                          </span>
                        </>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* --- What has been paid, with the statement for each ---------------------------- */}
          <section className="flex flex-col gap-2">
            <h2 className="text-sm font-semibold">{t('myEarnings.paidHeading')}</h2>
            {data.payouts.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('myEarnings.noPayoutsYet')}</p>
            ) : (
              <ul className="flex flex-col gap-2">
                {data.payouts.map((payout) => (
                  <li
                    key={payout.id}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border p-3"
                  >
                    <span className="min-w-0">
                      <span className="block font-medium">{formatDate(payout.paidAt)}</span>
                      <span className="block text-sm text-muted-foreground">
                        {payout.number} · {t('payouts.jobCount', { count: payout.jobs.length })}
                      </span>
                    </span>
                    <span className="flex items-center gap-3">
                      <span className="font-semibold tabular-nums">
                        {formatCurrency(payout.netPaise)}
                      </span>
                      <Button asChild variant="outline" size="sm">
                        <Link to={`/payouts/${payout.id}`}>{t('myEarnings.viewStatement')}</Link>
                      </Button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </>
  );
}

export default MyEarningsPage;
